// API Helper with automatic branch headers injection
// Use this for making API calls that need branch context
import { API_BASE_URL } from '@/config/api';
const API = API_BASE_URL;

/**
 * Get headers with branch context automatically injected
 * @param {Object} additionalHeaders - Extra headers to merge
 * @returns {Object} Headers object with Authorization, Merchant-ID, and Branch-ID
 */
export function getBranchHeaders(additionalHeaders = {}) {
  return {
    'Content-Type': 'application/json',
    ...getBranchAuthHeaders(),
    ...additionalHeaders
  };
}

/**
 * Auth + tenant headers only (no Content-Type) - safe for FormData uploads.
 * The server derives the restaurant from the token; X-Merchant-ID only picks
 * another location the signed-in owner manages (multi-location switcher).
 */
export function getBranchAuthHeaders() {
  const token = localStorage.getItem('backoffice_token');
  const merchantId = localStorage.getItem('backoffice_active_restaurant');
  const branchId = localStorage.getItem('current_branch_id');

  return {
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...(merchantId ? { 'X-Merchant-ID': merchantId } : {}),
    ...(branchId ? { 'X-Branch-Id': branchId } : {}),
  };
}

/**
 * Fetch wrapper with automatic branch headers
 * @param {string} url - API endpoint (with or without /api prefix)
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>}
 */
export async function branchFetch(url, options = {}) {
  const fullUrl = url.startsWith('http') ? url : `${API}${url.startsWith('/api') ? url : `/api${url}`}`;
  
  const headers = getBranchHeaders(options.headers);
  
  return fetch(fullUrl, {
    ...options,
    headers
  });
}

/**
 * JSON fetch wrapper - automatically parses response
 * @param {string} url - API endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise<any>} Parsed JSON response
 */
export async function branchFetchJson(url, options = {}) {
  const response = await branchFetch(url, options);
  
  if (!response.ok) {
    const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
    error.status = response.status;
    try {
      error.data = await response.json();
    } catch {
      error.data = null;
    }
    throw error;
  }
  
  return response.json();
}

/**
 * POST helper with automatic branch headers
 */
export async function branchPost(url, data, options = {}) {
  return branchFetchJson(url, {
    method: 'POST',
    body: JSON.stringify(data),
    ...options
  });
}

/**
 * PATCH helper with automatic branch headers
 */
export async function branchPatch(url, data, options = {}) {
  return branchFetchJson(url, {
    method: 'PATCH',
    body: JSON.stringify(data),
    ...options
  });
}

/**
 * DELETE helper with automatic branch headers
 */
export async function branchDelete(url, options = {}) {
  return branchFetch(url, {
    method: 'DELETE',
    ...options
  });
}

export default {
  getBranchHeaders,
  branchFetch,
  branchFetchJson,
  branchPost,
  branchPatch,
  branchDelete
};
